import { Component, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Language, LanguageService } from './shared/services';
import {
  AuthService,
  EUserRole,
  IUser,
  TokenStorageService,
  UserService,
} from './core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppStore } from './shared/state';
import { finalize } from 'rxjs';
import { MessageService } from './message';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environment/environment';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  private _translate = inject(TranslateService);
  private _languageService = inject(LanguageService);
  private _auth: AuthService = inject(AuthService);
  private _tokenStorage: TokenStorageService = inject(TokenStorageService);
  private _userService: UserService = inject(UserService);
  private readonly _error: MessageService = inject(MessageService);
  protected appStore: AppStore = inject(AppStore);

  public signIn = false;
  public hideBackButton = true;

  private _lang: Language | null = null;

  constructor() {
    console.log('ENVIROMENT', environment.name, environment);

    this._lang = this._languageService.language;
    if (!this._lang) {
      this._lang = 'es';
      this._languageService.setLanguage(this._lang);
    }

    this._translate.setDefaultLang('en');
    this._translate.use(this._lang);

    this._tokenStorage.checkAccessToWeb();

    this._auth
      .handleRedirectObservable()
      .pipe(untilDestroyed(this))
      .subscribe(userObj => {
        if (userObj) {
          this._auth.saveUser(userObj);
        }
      });

    this._auth.isAuthorized$
      .pipe(untilDestroyed(this))
      .subscribe(({ isAuth, isLoading }) => {
        this.signIn = isAuth;
        this.appStore.setPageLoading(isLoading);

        if (isAuth) {
          setTimeout(() => {
            this._getUser();
          }, 0);
        }
      });
  }

  public login() {
    this._auth.login();
  }

  private _getUser(): void {
    this.appStore.setPageLoading(true);
    this._userService
      .getUser()
      .pipe(
        finalize(() => this.appStore.setPageLoading(false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (user: IUser) => {
          localStorage.setItem('sessionUser', JSON.stringify(user));
          if (user.role === EUserRole.PATIENT) {
            this.signIn = false;
            this.hideBackButton = false;
            this._error.emitError(
              this._translate.instant('httpErrors.ROLE_NOT_SUPPORTED')
            );
          } else {
            this.appStore.setUser(user);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.appStore.setPageLoading(false);
          if (error.status === 401 && error.error.key === 'UNAUTHORIZED') {
            this.signIn = false;
            this.hideBackButton = false;
          } else if (
            error.status === 401 &&
            error.error.key === 'VALIDATION_USER_NOT_FOUND'
          ) {
            this.hideBackButton = false;
          }
        },
      });
  }

  public logout(): void {
    this._auth.logout();
  }
}
